import React, { useEffect, useState } from 'react';
import { X, Download, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ManualPurchaseResource, ManualPurchaseStatus } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../utils/helpers';

const STATUS_STYLE: Record<ManualPurchaseStatus, { label: string; className: string }> = {
  active: { label: 'Aktif', className: 'bg-green-100 text-green-700' },
  pending_registration: { label: 'Menunggu Daftar', className: 'bg-yellow-100 text-yellow-800' },
  cancelled: { label: 'Dibatalkan', className: 'bg-gray-100 text-gray-600' },
};

export const manualPurchaseStatusBadge = (status: ManualPurchaseStatus) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[status]?.className ?? 'bg-gray-100 text-gray-600'}`}>
    {STATUS_STYLE[status]?.label ?? status}
  </span>
);

interface Props {
  purchaseId: string | null;
  onClose: () => void;
  onChanged: () => void;
}

const errorMessage = (err: any, fallback: string): string => {
  const errors = err?.response?.data?.errors;
  if (errors) {
    const first = Object.values(errors)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
  }
  return err?.response?.data?.message || fallback;
};

const ManualPurchaseDetailSlideOver: React.FC<Props> = ({ purchaseId, onClose, onChanged }) => {
  const { canManage } = useAuth();
  const [purchase, setPurchase] = useState<ManualPurchaseResource | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [proof, setProof] = useState<{ url: string; type: string } | null>(null);

  useEffect(() => {
    if (!purchaseId) return;
    let objectUrl: string | null = null;
    setLoading(true);
    setMessage(null);
    setProof(null);
    api.getManualPurchase(purchaseId)
      .then(setPurchase)
      .catch((err) => setMessage({ type: 'error', text: errorMessage(err, 'Gagal memuat pembelian.') }))
      .finally(() => setLoading(false));
    // Bukti bayar di storage private: diambil sebagai blob dengan token admin.
    api.getManualPurchaseProof(purchaseId)
      .then((blob) => { objectUrl = URL.createObjectURL(blob); setProof({ url: objectUrl, type: blob.type }); })
      .catch(() => setProof(null));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [purchaseId]);

  if (!purchaseId) return null;

  const run = async (action: () => Promise<ManualPurchaseResource | void>, success: string) => {
    try {
      setBusy(true);
      setMessage(null);
      const result = await action();
      if (result) setPurchase(result);
      setMessage({ type: 'ok', text: success });
      onChanged();
    } catch (err: any) {
      setMessage({ type: 'error', text: errorMessage(err, 'Aksi gagal.') });
    } finally {
      setBusy(false);
    }
  };

  const changeEmail = () => {
    if (!purchase) return;
    const email = window.prompt('Email baru untuk pembelian ini:', purchase.email);
    if (!email || email.trim() === purchase.email) return;
    run(async () => (await api.changeManualPurchaseEmail(purchase.id, email.trim())).data, 'Email diubah dan email pembelian dikirim.');
  };

  const cancel = () => {
    if (!purchase || !window.confirm('Batalkan pembelian ini? Paket tidak akan aktif walau email ini mendaftar.')) return;
    run(() => api.cancelManualPurchase(purchase.id), 'Pembelian dibatalkan.');
  };

  const isPending = purchase?.status === 'pending_registration';

  return (
    <div className="fixed inset-0 overflow-hidden z-50">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div className="w-screen max-w-xl">
          <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto">
            <div className="px-6 py-5 bg-gray-50 border-b flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Detail pembelian manual</h2>
                {purchase && <p className="mt-1 text-sm text-gray-500">{purchase.email}</p>}
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            {loading || !purchase ? (
              <div className="p-6">{loading ? <LoadingSpinner /> : message && <p className="text-sm text-red-600">{message.text}</p>}</div>
            ) : (
              <div className="flex-1 px-6 py-5 space-y-6 text-sm">
                {message && (
                  <div className={`p-3 rounded-md ${message.type === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{message.text}</div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {manualPurchaseStatusBadge(purchase.status)}
                  {purchase.activatedAt && <span className="text-gray-500">aktif sejak {formatDate(purchase.activatedAt)}</span>}
                  {purchase.cancelledAt && <span className="text-gray-500">dibatalkan {formatDate(purchase.cancelledAt)}</span>}
                </div>

                {purchase.lastActivationError && isPending && (
                  <div className="flex gap-2 p-3 rounded-md bg-yellow-50 text-yellow-800">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div>Aktivasi otomatis gagal: {purchase.lastActivationError}</div>
                  </div>
                )}

                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 bg-gray-50 rounded-lg p-4">
                  <div><dt className="text-xs uppercase text-gray-500">Company</dt>
                    <dd className="font-medium text-gray-900">
                      {purchase.company ? <Link to={`/companies/${purchase.company.id}`} className="text-primary-600 hover:underline">{purchase.company.brandName}</Link> : 'Belum terdaftar'}
                    </dd></div>
                  <div><dt className="text-xs uppercase text-gray-500">Bundling</dt><dd className="font-medium text-gray-900">{purchase.bundle?.name ?? 'Tanpa bundling'}</dd></div>
                  <div><dt className="text-xs uppercase text-gray-500">Diinput oleh</dt><dd className="text-gray-900">{purchase.createdBy?.fullName ?? '-'}</dd></div>
                  <div><dt className="text-xs uppercase text-gray-500">Tanggal input</dt><dd className="text-gray-900">{formatDate(purchase.createdAt)}</dd></div>
                  {purchase.notes && <div className="col-span-2"><dt className="text-xs uppercase text-gray-500">Catatan</dt><dd className="text-gray-900">{purchase.notes}</dd></div>}
                </dl>

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Item</h3>
                  <table className="w-full">
                    <thead className="text-left text-xs text-gray-500"><tr><th className="py-1">Item</th><th>Harga</th><th>Berlaku sampai</th><th>Invoice</th></tr></thead>
                    <tbody className="divide-y">
                      {purchase.items.map((item) => (
                        <tr key={item.id}>
                          <td className="py-2 pr-2">{item.name} — {item.isLifetime ? 'Lifetime' : `${item.months} bln`}{item.isFromBundle ? '' : <span className="ml-1 text-xs text-gray-400">(tambahan)</span>}</td>
                          <td className="py-2 pr-2">
                            {formatCurrency(item.finalPrice)}
                            {item.discount > 0 && <div className="text-xs text-gray-400 line-through">{formatCurrency(item.normalPrice)}</div>}
                          </td>
                          <td className="py-2 pr-2">
                            {item.isLifetime ? 'Selamanya' : item.expiresAt ? formatDate(item.expiresAt) : `${item.months} bln sejak aktivasi`}
                          </td>
                          <td className="py-2 text-xs">
                            {item.subscriptionInvoice && <Link to="/subscription-invoices" className="text-primary-600 hover:underline">{item.subscriptionInvoice.invoiceNumber}</Link>}
                            {item.storeInvoice && <Link to="/store-invoices" className="text-primary-600 hover:underline">{item.storeInvoice.invoiceNumber}</Link>}
                            {!item.subscriptionInvoice && !item.storeInvoice && '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 space-y-1 border-t pt-3">
                    <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(purchase.subtotal)}</span></div>
                    {purchase.bundleDiscount > 0 && <div className="flex justify-between"><span className="text-gray-500">Diskon bundling</span><span className="text-red-600">−{formatCurrency(purchase.bundleDiscount)}</span></div>}
                    <div className="flex justify-between font-semibold"><span>Total bayar</span><span>{formatCurrency(purchase.grandTotal)}</span></div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Bukti bayar</h3>
                  {proof ? (
                    proof.type.startsWith('image/') ? (
                      <a href={proof.url} target="_blank" rel="noreferrer"><img src={proof.url} alt="Bukti bayar" className="max-h-72 rounded border" /></a>
                    ) : (
                      <a href={proof.url} download={purchase.paymentProofName ?? 'bukti-bayar'} className="inline-flex items-center gap-1 text-primary-600 hover:underline">
                        <Download className="h-4 w-4" /> {purchase.paymentProofName ?? 'Unduh bukti bayar'}
                      </a>
                    )
                  ) : <p className="text-gray-500">Bukti bayar tidak bisa dimuat.</p>}
                </div>

                {canManage && purchase.status !== 'cancelled' && (
                  <div className="border-t pt-4 flex flex-wrap gap-2">
                    <button disabled={busy} onClick={() => run(() => api.resendManualPurchaseEmail(purchase.id), 'Email pembelian dikirim ulang.')}
                      className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">Kirim ulang email</button>
                    {isPending && (
                      <>
                        <button disabled={busy} onClick={changeEmail} className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">Ubah email</button>
                        {purchase.lastActivationError && (
                          <button disabled={busy} onClick={() => run(() => api.retryManualPurchase(purchase.id), 'Pembelian berhasil diaktifkan.')}
                            className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50">Coba aktifkan lagi</button>
                        )}
                        <button disabled={busy} onClick={cancel} className="px-3 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50">Batalkan</button>
                      </>
                    )}
                  </div>
                )}
                {purchase.status === 'active' && (
                  <p className="text-xs text-gray-500">Pembatalan setelah aktif dilakukan lewat refund di Subscription Invoices dan menonaktifkan store purchase terkait.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualPurchaseDetailSlideOver;
